// Comprehensive retry mechanism components for failed operations

import React, { useState, useCallback, useEffect } from 'react';
import { useErrorHandling, useNetworkAwareOperation } from '../hooks/useErrorHandling';
import { LoadingSpinner, InlineLoading } from './LoadingStates';
import ErrorDisplay from './ErrorDisplay';
import type { AppError } from '../utils/errorHandling';

interface RetryableOperationProps<T> {
  operation: () => Promise<T>;
  onSuccess: (result: T) => void;
  onError?: (error: AppError) => void;
  maxRetries?: number;
  retryDelay?: number;
  children: (state: {
    execute: () => void;
    isLoading: boolean;
    error: AppError | null;
    canRetry: boolean;
    retryCount: number;
  }) => React.ReactNode;
  autoExecute?: boolean;
  showNetworkStatus?: boolean;
}

export function RetryableOperation<T>({
  operation,
  onSuccess,
  onError,
  maxRetries = 3,
  retryDelay = 1000,
  children,
  autoExecute = false,
  showNetworkStatus = true,
}: RetryableOperationProps<T>) {
  const {
    error,
    isLoading,
    retryCount,
    canRetry,
    executeWithErrorHandling,
    retry,
    clearError,
  } = useErrorHandling({
    maxRetries,
    retryDelay,
    onError,
    onSuccess: () => {},
  });

  const { isOnline, executeOrQueue } = useNetworkAwareOperation();

  const execute = useCallback(async () => {
    clearError();
    
    const result = await executeOrQueue(
      () => executeWithErrorHandling(operation, 'Retryable operation'),
      { executeOffline: false, queueWhenOffline: true }
    );
    
    if (result) {
      onSuccess(result);
    }
  }, [operation, onSuccess, executeWithErrorHandling, executeOrQueue, clearError]);

  const handleRetry = useCallback(async () => {
    try {
      const result = await retry(operation);
      if (result) {
        onSuccess(result);
      }
    } catch (retryError) {
      // Error is already handled by the retry mechanism
    }
  }, [retry, operation, onSuccess]);

  useEffect(() => {
    if (autoExecute) {
      execute();
    }
  }, [autoExecute, execute]);

  return (
    <>
      {showNetworkStatus && !isOnline && (
        <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/40 rounded-md">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-yellow-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-sm text-yellow-400">
              You're currently offline. Operations will be queued until connection is restored.
            </p>
          </div>
        </div>
      )}
      
      {children({
        execute,
        isLoading,
        error,
        canRetry,
        retryCount,
      })}
    </>
  );
}

interface AutoRetryWrapperProps<T> {
  operation: () => Promise<T>;
  onSuccess: (result: T) => void;
  onFinalError?: (error: AppError) => void;
  maxRetries?: number;
  retryDelay?: number;
  loadingMessage?: string;
  errorMessage?: string;
  showProgress?: boolean;
  className?: string;
}

export function AutoRetryWrapper<T>({
  operation,
  onSuccess,
  onFinalError,
  maxRetries = 3,
  retryDelay = 1000,
  loadingMessage = 'Loading...',
  errorMessage,
  showProgress = false,
  className = '',
}: AutoRetryWrapperProps<T>) {
  return (
    <RetryableOperation
      operation={operation}
      onSuccess={onSuccess}
      onError={onFinalError}
      maxRetries={maxRetries}
      retryDelay={retryDelay}
      autoExecute={true}
    >
      {({ isLoading, error, canRetry, retryCount, execute }) => {
        if (isLoading) {
          return (
            <div className={`flex flex-col items-center justify-center p-6 ${className}`}>
              <LoadingSpinner size="lg" className="mb-4" />
              <p className="text-navy-200 mb-2">{loadingMessage}</p>
              {showProgress && retryCount > 0 && (
                <p className="text-sm text-navy-300">
                  Retry attempt {retryCount} of {maxRetries}
                </p>
              )}
            </div>
          );
        }

        if (error) {
          return (
            <div className={className}>
              <ErrorDisplay
                error={error}
                onRetry={canRetry ? execute : undefined}
                variant="card"
              />
              {errorMessage && (
                <p className="text-sm text-navy-200 mt-2 text-center">
                  {errorMessage}
                </p>
              )}
            </div>
          );
        }

        return null;
      }}
    </RetryableOperation>
  );
}

interface ManualRetryButtonProps {
  operation: () => Promise<void>;
  onSuccess?: () => void;
  onError?: (error: AppError) => void;
  maxRetries?: number;
  retryDelay?: number;
  buttonText?: string;
  loadingText?: string;
  successText?: string;
  disabled?: boolean;
  className?: string;
  variant?: 'primary' | 'secondary' | 'danger';
}

export const ManualRetryButton: React.FC<ManualRetryButtonProps> = ({
  operation,
  onSuccess,
  onError,
  maxRetries = 3,
  retryDelay = 1000,
  buttonText = 'Retry',
  loadingText = 'Retrying...',
  successText = 'Success!',
  disabled = false,
  className = '',
  variant = 'primary',
}) => {
  const [showSuccess, setShowSuccess] = useState(false);

  const getVariantClasses = () => {
    switch (variant) {
      case 'primary':
        return 'bg-gold-500 hover:bg-gold-600 text-navy-950';
      case 'secondary':
        return 'bg-navy-600 hover:bg-navy-700 text-white';
      case 'danger':
        return 'bg-red-600 hover:bg-red-700 text-white';
      default:
        return 'bg-gold-500 hover:bg-gold-600 text-navy-950';
    }
  };

  const handleSuccess = useCallback(() => {
    setShowSuccess(true);
    onSuccess?.();
    
    // Hide success message after 2 seconds
    setTimeout(() => {
      setShowSuccess(false);
    }, 2000);
  }, [onSuccess]);

  return (
    <RetryableOperation
      operation={operation}
      onSuccess={handleSuccess}
      onError={onError}
      maxRetries={maxRetries}
      retryDelay={retryDelay}
    >
      {({ execute, isLoading, error, canRetry, retryCount }) => (
        <div className="space-y-2">
          <button
            onClick={execute}
            disabled={disabled || isLoading || !canRetry}
            className={`
              px-4 py-2 rounded-md font-medium transition-colors duration-200
              ${getVariantClasses()}
              ${(disabled || isLoading || !canRetry) ? 'opacity-50 cursor-not-allowed' : ''}
              ${className}
            `}
          >
            {isLoading ? (
              <span className="flex items-center">
                <LoadingSpinner size="sm" color="white" className="mr-2" />
                {loadingText}
              </span>
            ) : showSuccess ? (
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {successText}
              </span>
            ) : (
              buttonText
            )}
          </button>
          
          {retryCount > 0 && !isLoading && (
            <p className="text-xs text-navy-300">
              Attempt {retryCount} of {maxRetries}
            </p>
          )}
          
          {error && !canRetry && (
            <p className="text-xs text-red-400">
              Maximum retry attempts reached
            </p>
          )}
        </div>
      )}
    </RetryableOperation>
  );
};

interface BatchRetryOperationProps<T> {
  operations: Array<{
    id: string;
    operation: () => Promise<T>;
    label?: string;
  }>;
  onSuccess: (results: Array<{ id: string; result: T }>) => void;
  onError?: (errors: Array<{ id: string; error: AppError }>) => void;
  onProgress?: (completed: number, total: number) => void;
  maxRetries?: number;
  retryDelay?: number;
  maxConcurrent?: number;
  continueOnError?: boolean;
  children: (state: {
    execute: () => void;
    isLoading: boolean;
    progress: number;
    errors: Array<{ id: string; error: AppError }>;
    canRetry: boolean;
  }) => React.ReactNode;
}

export function BatchRetryOperation<T>({
  operations,
  onSuccess,
  onError,
  onProgress,
  maxRetries = 3,
  retryDelay = 1000,
  maxConcurrent = 3,
  continueOnError = true,
  children,
}: BatchRetryOperationProps<T>) {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState<Array<{ id: string; error: AppError }>>([]);
  const [retryCount, setRetryCount] = useState(0);

  const execute = useCallback(async () => {
    setIsLoading(true);
    setProgress(0);
    setErrors([]);
    
    const results: Array<{ id: string; result: T }> = [];
    const batchErrors: Array<{ id: string; error: AppError }> = [];
    let completed = 0;

    // Process operations in batches
    for (let i = 0; i < operations.length; i += maxConcurrent) {
      const batch = operations.slice(i, i + maxConcurrent);
      
      const batchPromises = batch.map(async ({ id, operation }) => {
        let attempts = 0;
        
        while (attempts < maxRetries) {
          try {
            const result = await operation();
            return { id, result, success: true };
          } catch (error) {
            attempts++;
            
            if (attempts < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, retryDelay));
            } else {
              const { classifyError } = await import('../utils/errorHandling');
              return { id, error: classifyError(error), success: false };
            }
          }
        }
      });

      const batchResults = await Promise.all(batchPromises);

      for (const result of batchResults) {
        if (result.success) {
          results.push({ id: result.id, result: result.result });
        } else {
          batchErrors.push({ id: result.id, error: result.error });
          
          if (!continueOnError) {
            setErrors(batchErrors);
            setIsLoading(false);
            onError?.(batchErrors);
            return;
          }
        }
        
        completed++;
        const progressPercent = (completed / operations.length) * 100;
        setProgress(progressPercent);
        onProgress?.(completed, operations.length);
      }
    }

    setErrors(batchErrors);
    setIsLoading(false);
    
    if (batchErrors.length > 0) {
      onError?.(batchErrors);
    }
    
    onSuccess(results);
  }, [operations, maxRetries, retryDelay, maxConcurrent, continueOnError, onSuccess, onError, onProgress]);

  const canRetry = !isLoading && retryCount < maxRetries;

  return children({
    execute,
    isLoading,
    progress,
    errors,
    canRetry,
  });
}

interface NetworkAwareRetryProps {
  children: React.ReactNode;
  fallbackMessage?: string;
  showQueueStatus?: boolean;
}

export const NetworkAwareRetry: React.FC<NetworkAwareRetryProps> = ({
  children,
  fallbackMessage = 'Some features may be limited while offline.',
  showQueueStatus = true,
}) => {
  const { isOnline, queuedOperationsCount } = useNetworkAwareOperation();

  return (
    <div>
      {!isOnline && (
        <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/40 rounded-md">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-yellow-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm text-yellow-400">{fallbackMessage}</p>
              {showQueueStatus && queuedOperationsCount > 0 && (
                <p className="text-xs text-yellow-400 mt-1">
                  {queuedOperationsCount} operation{queuedOperationsCount !== 1 ? 's' : ''} queued for when connection is restored.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {children}
    </div>
  );
};